/**
 * RTK Query endpoints — Insights (epic #680)
 *
 * Usage:
 *   const { data, isFetching } = useInsightListQuery({ filters: { score: 'red' } });
 *   const [sendFeedback] = useInsightFeedbackMutation();
 *
 * Insights are keyed by `fingerprint`, not an id: cluster ids are renumbered on
 * every run, so feedback given last night has to attach to something stable.
 */

import { baseApi } from './baseApi';
import { buildQueryString } from './utils';

const insightsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /su/insights
    insightList: builder.query({
      query: (params = {}) => ({
        url: `/su/insights${buildQueryString(params)}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ fingerprint }) => ({ type: 'Insight', id: fingerprint })),
              { type: 'Insight', id: 'LIST' },
            ]
          : [{ type: 'Insight', id: 'LIST' }],
    }),

    // GET /su/insights/summary
    insightSummary: builder.query({
      query: () => ({ url: '/su/insights/summary', method: 'GET' }),
      providesTags: [{ type: 'Insight', id: 'SUMMARY' }],
    }),

    // GET /su/insights/:fingerprint
    insight: builder.query({
      query: (fingerprint) => ({ url: `/su/insights/${fingerprint}`, method: 'GET' }),
      providesTags: (_result, _error, fingerprint) => [{ type: 'Insight', id: fingerprint }],
    }),

    // POST /su/insights/:fingerprint/feedback
    insightFeedback: builder.mutation({
      query: ({ fingerprint, status, note }) => ({
        url: `/su/insights/${fingerprint}/feedback`,
        method: 'POST',
        body: { status, note },
      }),
      // The list defaults to open insights and the summary counts them, so
      // acting on one changes both — invalidate the pair together or the tile
      // and the list underneath it disagree.
      invalidatesTags: (_result, _error, { fingerprint }) => [
        { type: 'Insight', id: fingerprint },
        { type: 'Insight', id: 'LIST' },
        { type: 'Insight', id: 'SUMMARY' },
      ],
    }),

    // GET /su/insights/context
    insightContext: builder.query({
      query: () => ({ url: '/su/insights/context', method: 'GET' }),
      providesTags: [{ type: 'Insight', id: 'CONTEXT' }],
    }),

    // PUT /su/insights/context
    saveInsightContext: builder.mutation({
      query: (data) => ({ url: '/su/insights/context', method: 'PUT', body: data }),
      invalidatesTags: [{ type: 'Insight', id: 'CONTEXT' }],
    }),

    // POST /su/insights/generate
    generateInsights: builder.mutation({
      query: () => ({ url: '/su/insights/generate', method: 'POST' }),
      // Deliberately does NOT invalidate the list: the run is asynchronous and
      // takes minutes, so refetching immediately would just re-read the old one.
    }),
  }),
  overrideExisting: false,
});

export const {
  useInsightListQuery,
  useInsightSummaryQuery,
  useInsightQuery,
  useInsightFeedbackMutation,
  useInsightContextQuery,
  useSaveInsightContextMutation,
  useGenerateInsightsMutation,
} = insightsApi;

export default insightsApi;
